import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import TutorialVideos from './sections/TutorialVideos';

const APP_STORE_URL =
  'https://apps.apple.com/us/app/facturas-simples/id6741133517?ppid=518f04bc-8fa7-4d62-a012-30441aee8baa';
const WINDOWS_DOWNLOAD_URL = 'https://sticongen.blob.core.windows.net/installer/FacturasSimples-Setup-latest.exe';

const HACIENDA_PORTAL = 'https://admin.factura.gob.sv';

// ── Accordion helper ──────────────────────────────────────────────────────────
function Accordion({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg mb-3 overflow-hidden">
      <button
        className="w-full flex justify-between items-center px-6 py-4 bg-white hover:bg-gray-50 text-left focus:outline-none"
        onClick={() => setOpen(!open)}
      >
        <span className="font-semibold text-gray-800">{title}</span>
        <svg
          className={`w-5 h-5 text-blue-600 transform transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-5 text-gray-600 bg-white border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
}

// ── DTE Types Table ────────────────────────────────────────────────────────────
const DTE_TYPES = [
  {
    code: '01',
    name: 'Factura',
    abbr: 'FAC',
    use: 'Venta a consumidor final (sin NRC)',
    notes: 'Documento más común. No genera crédito fiscal al receptor.',
  },
  {
    code: '03',
    name: 'Crédito Fiscal',
    abbr: 'CCF',
    use: 'Venta a contribuyente registrado (con NRC)',
    notes: 'Permite al comprador reclamar el IVA como crédito fiscal.',
  },
  {
    code: '04',
    name: 'Nota de Remisión',
    abbr: 'NR',
    use: 'Traslado de mercancías sin transferencia de propiedad',
    notes: 'Consignaciones, préstamos entre establecimientos propios.',
  },
  {
    code: '05',
    name: 'Nota de Crédito',
    abbr: 'NC',
    use: 'Corrección o reducción de un CCF previo',
    notes: 'Devoluciones, descuentos posteriores, corrección de precios.',
  },
  {
    code: '06',
    name: 'Nota de Débito',
    abbr: 'ND',
    use: 'Cargo adicional sobre un CCF previo',
    notes: 'Incrementos de precio, fletes no facturados, penalidades.',
  },
  {
    code: '08',
    name: 'Liquidación',
    abbr: 'CL',
    use: 'Liquidación de comisiones entre mandante y mandatario',
    notes: 'Agentes comerciales y mandatarios con retención de comisión.',
  },
  {
    code: '09',
    name: 'Comprobante de Retención',
    abbr: 'CR',
    use: 'Retención de IVA por agentes de retención',
    notes: 'Solo aplica a contribuyentes designados por Hacienda.',
  },
  {
    code: '11',
    name: 'Factura de Exportación',
    abbr: 'FE',
    use: 'Venta de bienes/servicios al exterior (tasa 0%)',
    notes: 'Sin IVA. Requiere datos del comprador extranjero.',
  },
  {
    code: '14',
    name: 'Sujeto Excluido',
    abbr: 'SE',
    use: 'Compra a persona sin NRC (sujeto excluido del IVA)',
    notes: 'Lo emite el comprador, no el vendedor. Trabajo agrícola informal, arrendamiento.',
  },
];

// ── Certification Steps ────────────────────────────────────────────────────────
const CERT_STEPS = [
  {
    n: 1,
    title: 'Requisitos previos',
    body: (
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>NIT activo ante el Ministerio de Hacienda</li>
        <li>NRC activo (registro como contribuyente de IVA)</li>
        <li>Ser contribuyente registrado (no aplica a consumidores finales)</li>
        <li>Código de actividad económica (CIIU)</li>
        <li>Correo electrónico activo para recibir notificaciones de Hacienda</li>
      </ul>
    ),
  },
  {
    n: 2,
    title: 'Registrarse en el portal de Hacienda',
    body: (
      <>
        <p className="mt-2">Ingrese a <a href={HACIENDA_PORTAL} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">admin.factura.gob.sv</a> y seleccione <strong>«Solicitud de Ingreso»</strong>.</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Complete NIT/DUI y contraseña</li>
          <li>Verifique su correo electrónico con el código enviado por Hacienda</li>
          <li>Active su cuenta desde el enlace de verificación</li>
        </ul>
      </>
    ),
  },
  {
    n: 3,
    title: 'Solicitar autorización DTE',
    body: (
      <>
        <p className="mt-2">Dentro del portal, vaya a <strong>«Solicitar Autorización»</strong> en el menú principal.</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Seleccione los tipos de DTE que desea autorizar para su empresa</li>
          <li>Hacienda asignará el rango de números de control</li>
        </ul>
      </>
    ),
  },
  {
    n: 4,
    title: 'Completar pruebas mínimas en ambiente de prueba',
    body: (
      <>
        <p className="mt-2">Hacienda exige un mínimo de documentos de prueba por tipo. Facturas Simples automatiza esto:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Instale la app y configure su empresa en <strong>ambiente de prueba</strong></li>
          <li>Vaya a <strong>Configuración → Proceso de Autorización a Producción</strong></li>
          <li>Seleccione «Procesar Todos» — la app genera los documentos requeridos automáticamente</li>
          <li>Espere confirmación de sincronización en el portal de Hacienda</li>
        </ul>
      </>
    ),
  },
  {
    n: 5,
    title: 'Obtener el certificado de firma electrónica',
    body: (
      <>
        <p className="mt-2">Una vez aprobadas las pruebas, Hacienda emite su certificado:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Descargue el archivo <strong>.p12</strong> desde el portal de Hacienda</li>
          <li>Guarde la contraseña del certificado en un lugar seguro</li>
          <li>El certificado es gratuito y tiene vigencia limitada (renueve antes de vencer)</li>
        </ul>
      </>
    ),
  },
  {
    n: 6,
    title: 'Instalar el certificado en Facturas Simples',
    body: (
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Vaya a <strong>Configuración → Detalles de Empresa → Certificado</strong></li>
        <li>Toque «Seleccionar Certificado» y elija el archivo .p12</li>
        <li>Ingrese la contraseña del certificado</li>
        <li>Verifique que el estado muestre ✅ (válido)</li>
      </ul>
    ),
  },
  {
    n: 7,
    title: 'Configurar credenciales de Hacienda',
    body: (
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Vaya a <strong>Configuración → Detalles de Empresa → Credenciales de Hacienda</strong></li>
        <li>Ingrese usuario (NIT o correo) y contraseña del portal de Hacienda</li>
        <li>Toque «Verificar» — la app intentará autenticarse con la API de Hacienda</li>
      </ul>
    ),
  },
  {
    n: 8,
    title: 'Completar el asistente de Acceso a Producción',
    body: (
      <>
        <p className="mt-2">Cuando Hacienda apruebe sus pruebas (normalmente 15–60 días):</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Vaya a <strong>Configuración → Acceso a Producción</strong></li>
          <li>El asistente guiará los pasos restantes</li>
          <li>Genere documentos adicionales si Hacienda lo requiere</li>
        </ul>
      </>
    ),
  },
  {
    n: 9,
    title: 'Validación por Hacienda',
    body: (
      <>
        <p className="mt-2">Hacienda revisa los documentos de prueba en el portal. Tiempos estimados:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><strong>Revisión inicial:</strong> 15–30 días hábiles</li>
          <li><strong>Correcciones solicitadas:</strong> plazo adicional de 15 días</li>
          <li><strong>Aprobación final:</strong> notificación por correo electrónico</li>
        </ul>
      </>
    ),
  },
  {
    n: 10,
    title: 'Cambiar empresa a entorno de producción',
    body: (
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Vaya a <strong>Configuración → Detalles de Empresa → Entorno</strong></li>
        <li>Cambie de «Prueba» a «Producción»</li>
        <li>Confirme el cambio — a partir de este momento los DTE son oficiales</li>
        <li>Los DTE de prueba no son válidos fiscalmente; solo los de producción lo son</li>
      </ul>
    ),
  },
];

// ── FAQ Items ──────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
  {
    q: '¿Cuánto cuesta Facturas Simples?',
    a: (
      <div>
        <p>Hay tres planes:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li><strong>Lite (Gratis):</strong> Todos los tipos de DTE soportados, 1 empresa, módulo de Compras, anuncios de video breves antes de sincronizar.</li>
          <li><strong>Sin Publicidad ($5.99/mes):</strong> Todo lo de Lite + hasta 2 empresas + sin anuncios.</li>
          <li><strong>Pro ($9.99/mes):</strong> Todo lo anterior + empresas ilimitadas + sincronización en la nube (Azure) + envío de correos + exportación de datos.</li>
        </ul>
      </div>
    ),
  },
  {
    q: '¿Los anuncios del plan Lite son obligatorios?',
    a: 'Sí. En el plan Lite, un breve video de máximo 15–30 segundos se muestra antes de sincronizar cada factura. Es la forma de sostener el servicio gratuito. Los planes Sin Publicidad y Pro eliminan los anuncios completamente.',
  },
  {
    q: 'Mi certificado aparece como «inválido» — ¿qué hago?',
    a: (
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li><strong>Certificado vencido:</strong> solicite uno nuevo en el portal de Hacienda.</li>
        <li><strong>Contraseña incorrecta:</strong> verifique mayúsculas/minúsculas.</li>
        <li><strong>Archivo corrupto:</strong> descargue el certificado nuevamente.</li>
        <li><strong>Formato incorrecto:</strong> asegúrese de usar .p12, no .cer.</li>
      </ul>
    ),
  },
  {
    q: '¿Qué hago si aparece "Error al autenticar usuario"?',
    a: (
      <ul className="list-disc pl-5 mt-2 space-y-1">
        <li>Verifique usuario y contraseña en <a href={HACIENDA_PORTAL} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">admin.factura.gob.sv</a>.</li>
        <li>Si funciona en el portal, actualice las credenciales en <strong>Configuración → Empresas → Credenciales de Hacienda</strong>.</li>
      </ul>
    ),
  },
  {
    q: '¿Qué significa "YA EXISTE UN REGISTRO CON ESE VALOR"?',
    a: 'El número de control del DTE ya existe en el sistema de Hacienda (puede ocurrir si se reintentó un envío). La app reintenta automáticamente con un nuevo número. Si persiste, contacte soporte desde el chat.',
  },
  {
    q: '¿Qué es el modo contingencia?',
    a: 'Cuando la API de Hacienda no está disponible, la app guarda el DTE localmente y los podas procesar cuando cuando se restablezca la conexión.',
  },
  {
    q: '¿Cuándo uso Factura vs Crédito Fiscal?',
    a: 'Use Factura (01) cuando el comprador es consumidor final (sin NRC). Use Crédito Fiscal (03) cuando el comprador es un contribuyente registrado (con NRC activo) que necesita IVA como crédito fiscal.',
  },
  {
    q: '¿Puedo usar la app en múltiples dispositivos?',
    a: 'Sí. La suscripción está vinculada a su cuenta de App Store o Google Play. El plan Pro incluye sincronización en la nube (Azure) para mantener datos consistentes entre dispositivos.',
  },
];

// ── Main Component ─────────────────────────────────────────────────────────────
function Documentation() {
  const [activeSection, setActiveSection] = useState('inicio');

  const sections = [
    { id: 'inicio', label: 'Primeros Pasos' },
    { id: 'certificacion', label: 'Guía de Certificación' },
    { id: 'tipos-dte', label: 'Tipos de DTE' },
    { id: 'punto-de-venta', label: 'Punto de Venta' },
    { id: 'compras', label: 'Compras' },
    { id: 'cobros-pagos', label: 'Cobros y Pagos' },
    { id: 'exportacion', label: 'Exportación' },
    { id: 'libros-iva', label: 'Libros de IVA' },
    { id: 'faq', label: 'Preguntas Frecuentes' },
    { id: 'soporte', label: 'Soporte' },
  ];

  const scrollTo = (id) => {
    setActiveSection(id);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top nav */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="font-bold text-xl text-blue-600">Facturas Simples</Link>
          <div className="hidden md:flex space-x-6 text-sm">
            {sections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`hover:text-blue-600 focus:outline-none ${activeSection === s.id ? 'text-blue-600 font-semibold' : 'text-gray-600'}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
          >
            Descargar
          </a>
        </div>
      </nav>

      {/* Hero */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold mb-4">Documentación</h1>
          <p className="text-blue-100 text-lg max-w-2xl mx-auto">
            Todo lo que necesita saber para configurar y usar Facturas Simples en El Salvador.
          </p>
          <div className="mt-8">
            <a
              href="/docs/manual/index.html"
              className="inline-flex items-center bg-white text-blue-700 hover:bg-blue-50 font-semibold px-6 py-3 rounded-lg shadow-sm transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              Ver Manual Completo
            </a>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 max-w-4xl">

        {/* ── Primeros Pasos ───────────────────────────────────────────────── */}
        <section id="inicio" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
            Primeros Pasos
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">1. Descargue la app</h3>
              <p className="text-gray-600 text-sm">Disponible en App Store (iOS), Google Play (Android) y Windows. El instalador de Windows se actualiza automáticamente sin perder sus datos.</p>
              <div className="mt-3 flex flex-col space-y-1">
                <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">
                  App Store (iOS) →
                </a>
                <a href={WINDOWS_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">
                  Windows (Instalador) →
                </a>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">2. Configure su empresa</h3>
              <p className="text-gray-600 text-sm">Ingrese NIT, NRC, actividad económica, dirección y correo electrónico de su empresa.</p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">3. Instale su certificado</h3>
              <p className="text-gray-600 text-sm">Cargue el certificado .p12 de Hacienda y configure sus credenciales para empezar a emitir.</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h4 className="font-semibold text-blue-800 mb-2">¿No tiene certificado aún?</h4>
            <p className="text-blue-700 text-sm">
              Consulte la sección <button onClick={() => scrollTo('certificacion')} className="underline font-medium focus:outline-none">Guía de Certificación</button> para el proceso completo. El certificado es gratuito y lo emite el Ministerio de Hacienda.
            </p>
          </div>

          <div className="mt-4 text-right">
            <a href="/docs/manual/auth-login.html" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">
              Ver más en el manual
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── Guía de Certificación ────────────────────────────────────────── */}
        <section id="certificacion" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
            Guía de Certificación
          </h2>
          <p className="text-gray-600 mb-6">
            Para emitir DTE en El Salvador, debe registrarse como emisor en el sistema de Hacienda. Este proceso se realiza una sola vez.
          </p>

          <div className="space-y-2">
            {CERT_STEPS.map((step) => (
              <Accordion key={step.n} title={`Paso ${step.n}: ${step.title}`} defaultOpen={step.n === 1}>
                <div className="pt-3">{step.body}</div>
              </Accordion>
            ))}
          </div>

          <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <p className="text-yellow-800 text-sm">
              <strong>Tiempo total estimado:</strong> El proceso completo desde el registro hasta la aprobación de Hacienda puede tomar entre 30 y 75 días hábiles, dependiendo de la velocidad de revisión de Hacienda.
            </p>
          </div>

          <div className="mt-4 text-right">
            <a href="/docs/manual/company-wizard.html" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">
              Ver más en el manual
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── Tipos de DTE ─────────────────────────────────────────────────── */}
        <section id="tipos-dte" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
            Tipos de DTE
          </h2>
          <p className="text-gray-600 mb-6">
            Facturas Simples soporta los principales Documentos Tributarios Electrónicos exigidos por el Ministerio de Hacienda de El Salvador, incluyendo facturas, crédito fiscal, notas, retenciones, liquidación, exportación, sujeto excluido y donación.
          </p>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 w-12">Cód.</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Nombre</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 hidden md:table-cell">Uso</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 hidden lg:table-cell">Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {DTE_TYPES.map((dt, i) => (
                    <tr key={dt.code} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded">
                          {dt.code}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-800">{dt.name}</div>
                        <div className="text-xs text-gray-500">{dt.abbr}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{dt.use}</td>
                      <td className="px-4 py-3 text-gray-500 hidden lg:table-cell text-xs">{dt.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-4 text-right">
            <a href="/docs/manual/factura-01.html" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">
              Ver más en el manual
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── Punto de Venta ─────────────────────────────────────────────────── */}
        <section id="punto-de-venta" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">4</span>
            Punto de Venta (POS)
          </h2>
          <p className="text-gray-600 mb-6">
            Venda al contado sin generar un DTE por cada operación: emita <strong>tickets no fiscales</strong>, descuente el stock automáticamente y consolide el día en una sola factura o CCF.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Register */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Registro de ventas</h3>
              <p className="text-gray-600 text-sm">
                Toque los productos para armar el ticket, cobre en efectivo (con cálculo de vuelto) o con datáfono, y comparta o imprima el recibo. El stock baja solo.
              </p>
            </div>

            {/* X/Z Closing */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Cierre de caja X/Z</h3>
              <p className="text-gray-600 text-sm">
                Consulta X para ver el corte del día sin cerrarlo, cierre Z para registrarlo y dejar constancia histórica por método de pago.
              </p>
            </div>

            {/* Consolidation */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Consolidación</h3>
              <p className="text-gray-600 text-sm">
                Agrupe los tickets del mes en un borrador de Factura o CCF con un solo toque; los tickets quedan marcados como facturados. Requiere plan de pago.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h4 className="font-semibold text-amber-800 mb-2">Documento no fiscal</h4>
              <p className="text-amber-700 text-sm">
                El ticket <strong>no es un DTE</strong>: no se envía a Hacienda. Por defecto queda fuera de los Libros de IVA hasta consolidarlo en una factura.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h4 className="font-semibold text-blue-800 mb-2">Impresión térmica</h4>
              <p className="text-blue-700 text-sm">
                En Escritorio configure una impresora térmica (red o del sistema) para tickets de 58/80&nbsp;mm; en Android comparta el recibo desde la hoja del sistema.
              </p>
            </div>
          </div>

          <div className="mt-4 text-right">
            <a href="/docs/manual/pos-overview.html" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">
              Ver más en el manual
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── Módulo de Compras ─────────────────────────────────────────────── */}
        <section id="compras" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">5</span>
            Módulo de Compras
          </h2>
          <p className="text-gray-600 mb-6">
            Registre y gestione las facturas que recibe de sus proveedores. Disponible en <strong>todos los planes</strong>, incluyendo Lite.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {/* Gmail Import */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Importar por correo (IMAP)</h3>
              <p className="text-gray-600 text-sm">
                Configure su correo con el asistente IMAP — Gmail con contraseña de aplicación por defecto, o cualquier otro proveedor — y la app importa automáticamente los DTE recibidos. Sincronización configurable: manual, cada hora, 6 o 12 horas.
              </p>
            </div>

            {/* OCR */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Escaneo OCR</h3>
              <p className="text-gray-600 text-sm">
                Tome una foto de la factura física y la app extrae automáticamente NIT, fecha y montos con reconocimiento óptico (ML Kit).
              </p>
              <span className="inline-block mt-3 text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded">Solo Android</span>
            </div>

            {/* Manual Entry */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Entrada Manual</h3>
              <p className="text-gray-600 text-sm">
                Ingrese los datos del emisor, fecha y montos manualmente. El IVA se calcula automáticamente. Disponible en todas las plataformas.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-green-50 border border-green-200 rounded-xl p-6">
              <h4 className="font-semibold text-green-800 mb-2">Resumen Mensual</h4>
              <p className="text-green-700 text-sm">
                Totales automáticos del mes: gravado, IVA crédito fiscal, total a pagar y cantidad de documentos. Útil para declaraciones mensuales.
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h4 className="font-semibold text-blue-800 mb-2">Exportar a Excel</h4>
              <p className="text-blue-700 text-sm">
                Exporte las compras del mes a un archivo <strong>.xlsx</strong> con todos los campos: emisor, tipo de DTE, totales, IVA y origen. Comparta por correo o guarde en su dispositivo.
              </p>
            </div>
          </div>

          <div className="mt-4 text-right">
            <a href="/docs/manual/compras-list.html" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">
              Ver más en el manual
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── Cobros y Pagos ───────────────────────────────────────────────── */}
        <section id="cobros-pagos" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">6</span>
            Cobros y Pagos
          </h2>
          <p className="text-gray-600 mb-6">
            Controle saldos locales de cuentas por cobrar y cuentas por pagar desde las facturas emitidas y compras recibidas.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V6m0 12v-2m8-4a8 8 0 11-16 0 8 8 0 0116 0z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Cuentas por cobrar</h3>
              <p className="text-gray-600 text-sm">
                Las ventas a crédito o ventas marcadas con saldo pendiente muestran cliente, DTE, vencimiento, total, abonado y saldo.
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 14h6m-7 4h8a2 2 0 002-2V7.5L14.5 4H8a2 2 0 00-2 2v10a2 2 0 002 2zM14 4v4h4" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Cuentas por pagar</h3>
              <p className="text-gray-600 text-sm">
                Las compras a crédito o con saldo pendiente alimentan obligaciones con proveedores y permiten registrar pagos parciales o completos.
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
            <p className="text-yellow-800 text-sm">
              <strong>Alcance actual:</strong> los pagos son locales. La sincronización en la nube, exportación de cuentas, asignación a múltiples documentos y anulación/undo de pagos quedan como funciones futuras.
            </p>
          </div>

          <div className="mt-4 text-right">
            <a href="/docs/manual/cuentas-overview.html" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">
              Ver más en el manual
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── Exportación de Documentos ────────────────────────────────────── */}
        <section id="exportacion" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">7</span>
            Exportación de Documentos
          </h2>
          <p className="text-gray-600 mb-6">
            Exporte sus facturas y compras en múltiples formatos. Filtre por mes, tipo de DTE y estado antes de exportar.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {/* Excel & JSON */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Excel y JSON</h3>
              <p className="text-gray-600 text-sm">
                Exporte sus facturas a <strong>.xlsx</strong> (compatible con Excel y Google Sheets) o <strong>JSON</strong> (datos estructurados). Seleccione mes, tipo de DTE y estado.
              </p>
              <span className="inline-block mt-3 text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded">Todos los planes</span>
            </div>

            {/* Batch PDF */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">PDF por Lotes</h3>
              <p className="text-gray-600 text-sm">
                Genere un archivo <strong>ZIP</strong> con los PDFs de todas las facturas del período seleccionado de una sola vez.
              </p>
              <span className="inline-block mt-3 text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">Plan Pro</span>
            </div>

            {/* Google Drive */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Google Drive</h3>
              <p className="text-gray-600 text-sm">
                Suba sus exportaciones directamente a Google Drive con un toque. La autorización se realiza una sola vez por dispositivo.
              </p>
              <span className="inline-block mt-3 text-xs font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">Plan Pro</span>
            </div>

            {/* Email */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Enviar por Email</h3>
              <p className="text-gray-600 text-sm">
                Comparta el PDF y JSON de cualquier factura por correo electrónico desde el menú de opciones o la vista previa PDF.
              </p>
              <span className="inline-block mt-3 text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded">Todos los planes</span>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <h4 className="font-semibold text-blue-800 mb-2">¿Cómo exportar?</h4>
            <p className="text-blue-700 text-sm">
              Desde la pantalla de <strong>Facturas</strong>, presione el botón <strong>"Exportar"</strong>. Seleccione el mes, filtre por tipo de DTE y estado, y elija el formato deseado. El archivo se comparte a través del sistema del dispositivo (Share Sheet en iOS/Android, diálogo de guardado en Windows).
            </p>
          </div>

          <div className="mt-4 text-right">
            <a href="/docs/manual/invoice-export.html" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">
              Ver más en el manual
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── Libros de IVA ────────────────────────────────────────────────── */}
        <section id="libros-iva" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-content-center text-sm font-bold mr-3">8</span>
            Libros de IVA — Anexos F-07
          </h2>
          <p className="text-gray-600 mb-6">
            Genere los cinco archivos CSV exigidos por el Ministerio de Hacienda para la declaración mensual del IVA
            (formulario F-07): ventas a contribuyentes, ventas a consumidor final, compras, sujetos excluidos y documentos anulados.
          </p>

          {/* Overview cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {[
              { num: '1', label: 'Ventas a Contribuyentes', desc: 'CCFs emitidos con estado Completada. Una fila por factura.' },
              { num: '2', label: 'Ventas a Consumidor Final', desc: 'Facturas ordinarias y de exportación, agrupadas por día.' },
              { num: '3', label: 'Compras a Contribuyentes', desc: 'Compras con CCF o factura del proveedor. Incluye crédito fiscal.' },
              { num: '5', label: 'Compras a Sujetos Excluidos', desc: 'Compras con documentos DTE-14.' },
              { num: '🗓', label: 'Documentos Anulados', desc: 'DTEs anulados en el período, sin importar el tipo.' },
            ].map(a => (
              <div key={a.num} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center font-bold text-green-700 text-sm mb-3">
                  {a.num}
                </div>
                <div className="font-semibold text-gray-800 text-sm mb-1">{a.label}</div>
                <div className="text-gray-500 text-xs leading-relaxed">{a.desc}</div>
              </div>
            ))}
          </div>

          {/* How to generate */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-4">
            <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Cómo generar los Libros de IVA
            </h4>
            <ol className="text-green-700 text-sm space-y-2 list-none">
              {[
                'Desde Facturas o Compras, presione el ícono de Libros de IVA en la barra de acciones.',
                'Seleccione el mes y año del período a declarar.',
                'Revise la vista previa: número de facturas y compras en el período.',
                'Presione "Generar Anexos F-07".',
                'Comparta o descargue los archivos CSV generados.',
                'Suba los archivos al portal del MH (transparenciafiscal.gob.sv).',
              ].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-green-200 text-green-800 text-xs flex items-center justify-center font-bold">
                    {i + 1}
                  </span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Disclaimer */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 mb-4 flex gap-3">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-yellow-800 text-sm">
              <strong>Recomendación:</strong> Solicite a su contador que revise los archivos antes de subirlos al portal del MH.
              Los valores de Tipo de Operación predeterminados son para actividad comercial. Consulte con su contador si su actividad es diferente.
            </p>
          </div>

          <div className="mt-4 text-right">
            <a href="/docs/manual/que-son-los-libros-de-iva.html" className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline">
              Ver guía completa de Libros de IVA
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </a>
          </div>
        </section>

        {/* ── Preguntas Frecuentes ─────────────────────────────────────────── */}
        <section id="faq" className="mb-16 scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">9</span>
            Preguntas Frecuentes
          </h2>
          <p className="text-gray-600 mb-6">Respuestas a las consultas más comunes sobre Facturas Simples.</p>

          <div className="space-y-2">
            {FAQ_ITEMS.map((item, i) => (
              <Accordion key={i} title={item.q}>
                <div className="pt-3 text-gray-600">{typeof item.a === 'string' ? <p>{item.a}</p> : item.a}</div>
              </Accordion>
            ))}
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-600 mb-3">¿No encontró su pregunta?</p>
            <Link
              to="/faq"
              className="inline-block border border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-lg font-semibold mr-3"
            >
              Ver FAQ completo
            </Link>
          </div>
        </section>

        {/* ── Soporte ──────────────────────────────────────────────────────── */}
        <section id="soporte" className="scroll-mt-20">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
            <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3">10</span>
            Soporte
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Chat de Soporte (24/7)</h3>
              <p className="text-gray-600 text-sm mb-4">
                Nuestro asistente de IA está disponible en cualquier momento desde la app o desde esta página.
              </p>
              <Link
                to="/#soporte"
                className="block w-full bg-green-600 hover:bg-green-700 text-white text-center px-4 py-2 rounded-lg font-semibold text-sm"
              >
                Abrir Chat de Soporte
              </Link>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-800 mb-2">Contacto por correo</h3>
              <p className="text-gray-600 text-sm mb-4">
                Para problemas técnicos complejos o consultas de facturación, contáctenos directamente.
              </p>
              <Link
                to="/contact"
                className="block w-full bg-blue-600 hover:bg-blue-700 text-white text-center px-4 py-2 rounded-lg font-semibold text-sm"
              >
                Enviar mensaje
              </Link>
            </div>
          </div>

          <div className="mt-6 bg-gray-100 rounded-xl p-5">
            <h4 className="font-semibold text-gray-700 mb-2">Ministerio de Hacienda</h4>
            <div className="text-gray-600 text-sm space-y-1">
              <p>Portal oficial: <a href={HACIENDA_PORTAL} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">admin.factura.gob.sv</a></p>
              <p>Teléfono: 2244-3000</p>
              <p>Para problemas con la aprobación de certificados o rechazos del portal, contacte directamente a Hacienda.</p>
            </div>
          </div>
        </section>

        <TutorialVideos />

      </div>

      {/* Manual de Usuario CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-blue-100 py-12">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Manual de Usuario</h3>
                </div>
                <p className="text-gray-600 text-sm mb-4">
                  Documentación completa con capturas de pantalla, guías paso a paso y referencia de todas las funciones de Facturas Simples.
                </p>
                <div className="flex flex-wrap gap-3">
                  <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    iOS
                  </span>
                  <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                    </svg>
                    Android
                  </span>
                  <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Windows
                  </span>
                  <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-3 py-1.5 rounded-full">
                    <svg className="w-3.5 h-3.5 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    Web
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <a
                  href="/docs/manual/index.html"
                  className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-sm"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Abrir Manual
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8 mt-16">
        <div className="container mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">
            © 2025 Facturas Simples de K Labs Sa de CV. Todos los derechos reservados.
          </p>
          <div className="mt-3 flex justify-center space-x-6 text-sm">
            <a href="https://calm-field-062a3c510.3.azurestaticapps.net/privacy.html" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">Privacidad</a>
            <a href="https://calm-field-062a3c510.3.azurestaticapps.net/index.html" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white">Términos</a>
            <Link to="/contact" className="text-gray-400 hover:text-white">Contacto</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Documentation;
